import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { Sidebar } from '../../components/Sidebar';
import { ClipboardCheck, Inbox, ArrowUpRight, FileVideo, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const BrandApprovals = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/brand/pending-reviews`, { withCredentials: true });
      setItems(Array.isArray(data) ? data : []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    const onRefresh = () => load();
    window.addEventListener('brand:approvals-refresh', onRefresh);
    return () => {
      clearInterval(t);
      window.removeEventListener('brand:approvals-refresh', onRefresh);
    };
  }, [load]);

  return (
    <div className="flex min-h-screen">
      <Sidebar />
      <div className="flex-1 p-8 bg-white dark:bg-[#0A0B0D]" data-testid="brand-approvals-page">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
            <div>
              <h1 className="text-4xl font-bold tracking-tight text-[#0F0F0F] dark:text-white mb-2 flex items-center gap-3">
                <ClipboardCheck className="text-[#FF451A]" size={32} />
                Approvals
              </h1>
              <p className="text-[#6B6B6B] dark:text-zinc-400">Every deliverable waiting on your sign-off, across every campaign.</p>
            </div>
            {!loading && (
              <div
                className="px-4 py-2 rounded-full bg-[#FF451A]/10 border border-[#FF451A]/30 text-[#FF451A] text-sm font-mono font-semibold"
                data-testid="approvals-total-chip"
              >
                {items.length} pending
              </div>
            )}
          </div>

          {loading ? (
            <div className="py-20 text-center">
              <Loader2 className="inline-block animate-spin text-[#FF451A]" size={28} />
            </div>
          ) : items.length === 0 ? (
            <div
              className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-2xl p-16 text-center"
              data-testid="approvals-empty-state"
            >
              <Inbox className="mx-auto text-[#6B6B6B] dark:text-zinc-500 mb-3" size={44} />
              <h3 className="text-lg font-semibold text-[#0F0F0F] dark:text-white mb-1">No deliverables pending review</h3>
              <p className="text-sm text-[#6B6B6B] dark:text-zinc-400">When creators upload drafts, they'll show up here for one-tap review.</p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="approvals-list">
              {items.map((d) => {
                const uploadedAt = d.latest_submission?.uploaded_at || d.updated_at;
                const sinceLabel = (() => {
                  try {
                    return formatDistanceToNow(new Date(uploadedAt), { addSuffix: true });
                  } catch {
                    return '';
                  }
                })();
                const versionLabel = d.current_version > 0 ? `v${d.current_version}` : 'attached file';
                return (
                  <div
                    key={d.id}
                    className="group bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-2xl p-5 hover:border-[#FF451A]/30 hover:bg-[#0F0F0F]/80 transition-all flex items-center gap-5 flex-wrap"
                    data-testid="approval-card"
                  >
                    <div className="w-12 h-12 rounded-xl bg-[#FF451A]/10 flex items-center justify-center text-[#FF451A] shrink-0">
                      <FileVideo size={20} />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                        <span className="text-[10px] uppercase tracking-wider text-[#FF451A] font-semibold bg-[#FF451A]/10 border border-[#FF451A]/20 px-2 py-0.5 rounded">
                          {d.content_type}
                        </span>
                        <span className="text-[10px] uppercase tracking-wider text-[#0055FF] font-semibold bg-[#0055FF]/10 border border-[#0055FF]/20 px-2 py-0.5 rounded font-mono">
                          {versionLabel}
                        </span>
                        <span className="text-[10px] uppercase tracking-wider text-[#FFB800] font-semibold bg-[#FFB800]/10 border border-[#FFB800]/20 px-2 py-0.5 rounded">
                          Pending Review
                        </span>
                      </div>
                      <div className="text-base font-semibold text-[#0F0F0F] dark:text-white truncate" data-testid="approval-card-title">
                        {d.description || d.content_type}
                      </div>
                      <div className="text-xs text-[#6B6B6B] dark:text-zinc-400 mt-1 flex items-center gap-1.5 flex-wrap">
                        <span className="font-semibold text-zinc-200">{d.creator_name}</span>
                        <span className="text-[#A1A1AA] dark:text-zinc-600">·</span>
                        <span>{d.campaign_name}</span>
                        {sinceLabel && (
                          <>
                            <span className="text-[#A1A1AA] dark:text-zinc-600">·</span>
                            <span className="font-mono text-[#6B6B6B] dark:text-zinc-500">uploaded {sinceLabel}</span>
                          </>
                        )}
                      </div>
                    </div>

                    <Link
                      to={`/brand/campaigns?focus=${d.id}&bc=${d.brand_campaign_id || ''}&child=${d.campaign_id}`}
                      className="bg-[#FF451A] text-[#0F0F0F] dark:text-white rounded-full px-4 py-2 text-sm font-semibold hover:bg-[#E63E17] transition-colors flex items-center gap-1.5 shadow-[0_4px_20px_rgba(255,69,26,0.25)]"
                      data-testid="approval-open-button"
                    >
                      Open <ArrowUpRight size={14} />
                    </Link>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
